import { LoadingButton } from '@mui/lab'
import { Breadcrumbs, Button, Container, Link, Skeleton, Stack } from '@mui/material'
import { downloadTextFile } from 'consts/Theme'
import { useInventory } from 'features/Inventories/queries/useInventory'
import { useInventoryReport } from 'features/Inventories/queries/useInventoryReport'
import { SCOPE_THREE_ROUTES } from 'navigation/types/scopeThreeRoutes'
import { useTranslation } from 'react-i18next'
import {
  Outlet,
  Link as RRLink,
  generatePath,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom'

const InventoryTabButton = ({ path }: { path: 'boundary' | 'dashboard' }) => {
  const { t } = useTranslation('impact/InventoriesLayout')
  const navigate = useNavigate()
  const { inventoryId } = useParams()

  const toRoute = generatePath(`${SCOPE_THREE_ROUTES.DETAIL}/${path}`, { inventoryId })
  const match = useMatch(toRoute)

  return (
    <Button
      variant={match ? 'contained' : undefined}
      color={match ? 'secondary' : 'primary'}
      size="small"
      onClick={() => match || navigate(toRoute)}
      sx={{ boxShadow: 0 }}
    >
      {t(path)}
    </Button>
  )
}

export const InventoriesLayout = () => {
  const { t } = useTranslation('impact/InventoriesLayout')
  const { inventoryId } = useParams()
  const { data: inventory, isLoading } = useInventory(inventoryId)
  const inventoryReport = useInventoryReport(inventoryId)

  const isCreatePath = useMatch(SCOPE_THREE_ROUTES.CREATE)
  const isSetupPath = useMatch(
    generatePath(SCOPE_THREE_ROUTES.SETUP, { inventoryId: inventoryId ?? '' })
  )
  const isTargetsPath = useMatch(
    generatePath(SCOPE_THREE_ROUTES.TARGETS, { inventoryId: inventoryId ?? '' })
  )
  const isOfftakePath = useMatch(
    generatePath(SCOPE_THREE_ROUTES.OFFTAKE, { inventoryId: inventoryId ?? '' })
  )

  return (
    <>
      <Container style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Stack justifyContent="space-between" direction="row" alignItems="center" pr={3}>
          <Breadcrumbs
            sx={{
              p: 3,
              color: 'text.secondary',
              '.MuiBreadcrumbs-li:last-child': { color: 'text.primary' },
            }}
          >
            <Link component={RRLink} to={SCOPE_THREE_ROUTES.ROOT} underline="hover" color="inherit">
              {t('inventories')}
            </Link>
            {inventoryId ? (
              isLoading ? (
                <Skeleton width="140px" />
              ) : (
                <Link
                  component={RRLink}
                  underline="hover"
                  to={generatePath(SCOPE_THREE_ROUTES.DETAIL, {
                    inventoryId,
                  })}
                  aria-current="page"
                  color="inherit"
                >
                  {inventory?.item.name}
                </Link>
              )
            ) : null}
            {isCreatePath && (
              <Link underline="none" color="inherit">
                {t('newInventory')}
              </Link>
            )}
            {!!inventoryId && isSetupPath && (
              <Link underline="none" color="inherit">
                {t('setup')}
              </Link>
            )}
            {!!inventoryId && isTargetsPath && (
              <Link underline="none" color="inherit">
                {t('setTargets')}
              </Link>
            )}
            {!!inventoryId && isOfftakePath && (
              <Link underline="none" color="inherit">
                {t('offtake')}
              </Link>
            )}
          </Breadcrumbs>

          {!!inventory?.item?.years?.length && (
            <Stack direction="row" spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <InventoryTabButton path={'dashboard'} />
                <InventoryTabButton path={'boundary'} />
              </Stack>

              {isLoading ? (
                <Skeleton height="30px" width="180px" />
              ) : (
                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={inventoryReport.isLoading}
                  onClick={() => {
                    if (!inventoryReport.data?.reportStr) return

                    downloadTextFile({
                      textContent: inventoryReport.data?.reportStr,
                      filename: `${inventory?.item.name.replaceAll(' ', '_')}.csv`,
                      mimetype: 'text/csv',
                    })
                  }}
                  disabled={!inventoryReport.data?.reportStr}
                >
                  {t('download')}
                </LoadingButton>
              )}
            </Stack>
          )}
        </Stack>
      </Container>
      <Outlet />
    </>
  )
}
